// The per-site Settings hub gathers the configuration surfaces that used to
// hide behind small text links ("Edit Site Configuration" on the battery
// card, "View & Edit" on the tuning card, Usage Modes, email notifications)
// into one navigable place. Sections are defined here — pure data — so the
// hub page and tests stay in sync, and Jest (node env, no jsdom) can cover
// the link targets without rendering.

export interface SiteSettingsSection {
  key: string;
  title: string;
  // One plain-English sentence in owner language: what lives there, not how
  // it works. Where the destination has a card heading (Usage Modes, Tuning
  // Parameters, P2P Trading, Email Notifications) the title matches it so
  // owners can recognise where they landed.
  description: string;
  href: string;
}

// `isLocalVolts` defaults to true for callers that haven't resolved the
// site yet.
export function siteSettingsSections(
  siteId: number | string,
  opts: { isLocalVolts?: boolean } = {},
): SiteSettingsSection[] {
  const { isLocalVolts = true } = opts;
  return [
    {
      key: 'site-configuration',
      title: 'Site configuration',
      description: 'Your address, tariff, retailer and hardware details — what Powston uses to plan trading.',
      href: `/site/${siteId}/edit`,
    },
    {
      key: 'usage-modes',
      title: 'Usage Modes',
      description: 'Switch the trading strategy your battery follows, or create and test a new one.',
      // ModeSelector moved from the summary page to the Controls page.
      href: `/controls/${siteId}#usage-modes`,
    },
    // Tuning has its own page.
    {
      key: 'tuning-parameters',
      title: 'Tuning',
      description: 'Fine-tune how your battery trades — reserve levels, export margins and charge limits.',
      href: `/site/${siteId}/tuning`,
    },
    // P2P is the LocalVolts marketplace — omit on non-LocalVolts sites. Links
    // land on the getting-started guide, which sits just above the settings
    // card, so new owners learn how it works before configuring windows.
    ...(isLocalVolts ? [
      {
        key: 'p2p-trading',
        title: 'P2P Trading',
        description: 'Learn how peer-to-peer trading works and set the export windows you pledge.',
        href: `/summary/${siteId}#p2p-help-card`,
      },
      {
        key: 'p2p-marketplace-guide',
        title: 'LocalVolts Marketplace Guide',
        description: 'Step-by-step walkthrough of posting a sell offer on the LocalVolts market.',
        href: `/p2p_guide/${siteId}`,
      },
    ] : []),
    {
      key: 'notifications',
      title: 'Email Notifications',
      description: 'Choose which alerts and summaries Powston emails you.',
      href: '/profile#notifications',
    },
  ];
}

// Where the top-level /settings entry should land. With exactly one site
// there is nothing to choose, so go straight to that site's hub; otherwise
// the caller shows a picker (or, with no sites, points at onboarding).
export function settingsLandingPath(sites: Array<{ id: number }>): string | null {
  if (sites.length === 1) {
    return `/site/${sites[0].id}/settings`;
  }
  return null;
}

// True when a URL fragment addresses the given section anchor.
export function hashTargets(hash: string | null | undefined, anchorId: string): boolean {
  if (!hash) return false;
  return hash.replace(/^#/, '') === anchorId;
}
